import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import translations from "./translations";

const STORAGE_KEY = "azateser-lang";
const DEFAULT_LANG = "en";

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  t: translations[DEFAULT_LANG],
  toggleLang: () => {},
});

const readInitialLang = () => {
  // ?lang=tr wins: it is the URL search engines are given for the Turkish
  // version, so it has to work on a cold visit.
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("lang");
    if (fromUrl === "en" || fromUrl === "tr") return fromUrl;
  } catch (e) {
    // malformed query string — ignore and fall through
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "en" || stored === "tr") return stored;
  } catch (e) {
    // private mode / storage blocked — fall through to the default
  }

  return DEFAULT_LANG;
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(readInitialLang);

  // Keep <html lang> honest so screen readers and search engines agree with
  // what is actually on the page.
  useEffect(() => {
    document.documentElement.lang = lang;

    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch (e) {
      // nothing to do — the choice just will not survive a reload
    }

    // Reflect the choice in the URL so the link people share opens in the
    // language they were reading, and matches the hreflang alternates.
    try {
      const url = new URL(window.location.href);
      if (lang === DEFAULT_LANG) {
        url.searchParams.delete("lang");
      } else {
        url.searchParams.set("lang", lang);
      }
      window.history.replaceState(null, "", url.pathname + url.search + url.hash);
    } catch (e) {
      // history API unavailable — the language still applies, just not the URL
    }
  }, [lang]);

  const toggleLang = useCallback(() => {
    setLang((current) => (current === "en" ? "tr" : "en"));
  }, []);

  return (
    <LanguageContext.Provider value={{ lang, t: translations[lang], toggleLang }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);

export default LanguageContext;
